<template>
  <div class="rc-page">
    <!-- Hero banner -->
    <page-hero
        class="mb-1"
        :title="msg('Rent contracts')"
        :subtitle="msg('Manage reservations and bookings')"
        variant="contract"
        :stats="[{ icon: 'FileTextIcon', value: page.total, label: msg('total') }]"
    >
      <template #illustration>
        <svg viewBox="0 0 100 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="15" y="8" width="70" height="95" rx="6" fill="rgba(255,255,255,.15)" stroke="rgba(255,255,255,.25)" stroke-width="1.5"/>
          <rect x="28" y="24" width="32" height="3" rx="1.5" fill="rgba(255,255,255,.4)"/>
          <rect x="28" y="34" width="44" height="2.5" rx="1.25" fill="rgba(255,255,255,.25)"/>
          <rect x="28" y="42" width="38" height="2.5" rx="1.25" fill="rgba(255,255,255,.25)"/>
          <line x1="28" y1="58" x2="72" y2="58" stroke="rgba(255,255,255,.2)" stroke-width="1" stroke-dasharray="3 2"/>
          <rect x="28" y="68" width="24" height="8" rx="4" fill="rgba(255,255,255,.3)"/>
          <circle cx="82" cy="25" r="8" fill="rgba(255,255,255,.08)"/>
          <circle cx="8" cy="85" r="10" fill="rgba(255,255,255,.06)"/>
        </svg>
      </template>
    </page-hero>

    <!-- Action buttons (moved from header) -->
    <div class="d-flex align-items-center flex-wrap mb-1" style="gap:8px">
      <b-button variant="outline-danger" class="rc-btn" size="sm" @click="onBlockCalendar()">
        <i class="fas fa-ban mr-50"></i> {{ msg('blockCalendar') }}
      </b-button>
      <b-button :disabled="isApaleoBranchOnly" variant="primary" class="rc-btn" size="sm" @click="onTreasureContractGenerateMulti()">
        <i class="fas fa-plus mr-50"></i> {{ msg('Create contract') }}
      </b-button>
      <b-button variant="outline-primary" class="rc-btn" size="sm" @click="refresh()">
        <i class="fas fa-sync-alt mr-50"></i> {{ msg('Reload') }}
      </b-button>
    </div>

    <!-- Filters — primary bar + collapsible advanced filters + active chips -->
    <div class="rc-filter-wrap" v-bind:class="{'sticky':stickyFilter}">
      <div class="rcx-filter">
        <!-- header -->
        <div class="rcx-filter__head">
          <span class="rcx-filter__badge"><i class="fas fa-sliders-h"/></span>
          <span class="rcx-filter__title">{{ msg('Filters') }}</span>
          <b-badge v-if="activeFilters.length" variant="light-primary" pill class="ml-50">
            {{ activeFilters.length }} {{ msg('active') }}
          </b-badge>
          <span class="rcx-filter__line"/>
          <button type="button" class="rcx-icon-btn" :class="{'rcx-icon-btn--on': stickyFilter}"
                  v-b-tooltip.hover :title="msg('Pin filters')" @click="stickyFilter = !stickyFilter">
            <i class="fa-solid fa-thumbtack"/>
          </button>
          <button type="button" class="rcx-icon-btn" v-b-tooltip.hover :title="msg('Clear all')" @click="clearAllFilters">
            <i class="fas fa-eraser"/>
          </button>
          <button type="button" class="rcx-icon-btn" v-b-tooltip.hover :title="msg('Reload')" @click="refresh()">
            <i class="fas fa-redo" :class="{'fa-spin': loading}"/>
          </button>
        </div>

        <!-- primary row: the filters staff actually reach for daily -->
        <div class="rcx-grid rcx-grid--primary">
          <div class="rcx-field rcx-field--range">
            <label class="rcx-label"><i class="far fa-calendar-alt mr-25"/>{{ msg('checkinrange') }}</label>
            <div class="rcx-range">
              <calendar-picker-range size="sm" v-model="datesRange"/>
              <button v-if="datesRange" type="button" class="rcx-range__clear" v-b-tooltip.hover :title="msg('Reset')" @click="datesRange=null">
                <i class="fas fa-times"/>
              </button>
            </div>
          </div>
          <div class="rcx-field">
            <label class="rcx-label"><i class="fas fa-signal mr-25"/>{{ msg('Status') }}</label>
            <v-select multiple :dir="isRTL ? 'rtl' : 'ltr'" :clearable="true" v-model="filter.in.status" :options="allContractStatus" class="rcx-select w-100" label="text" :reduce="(s) => s.value" />
          </div>
          <div class="rcx-field">
            <label class="rcx-label"><i class="fas fa-globe mr-25"/>{{ msg('Platform') }}</label>
            <v-select :dir="isRTL ? 'rtl' : 'ltr'" v-model="filter.eq.bnbStyleRent.platform" :options="[{value:'Booking',label:'Booking'},{value:'Airbnb',label:'Airbnb'},{value:'Treasure Home',label:'Treasure Home'},{value:'Expedia',label:'Expedia'},{value:'Vrbo',label:'Vrbo'},{value:'Agoda',label:'Agoda'},{value:null,label:'All'}]" :clearable="false" label="label" :reduce="(s) => s.value" class="rcx-select w-100" />
          </div>
          <div class="rcx-field">
            <label class="rcx-label"><i class="fas fa-hashtag mr-25"/>{{ msg('Id') }}</label>
            <b-input-group size="sm" class="rcx-group">
              <b-input-group-prepend is-text><i class="fas fa-search"/></b-input-group-prepend>
              <b-form-input v-model="filter.like.id" :placeholder="msg('Search by ID')"/>
            </b-input-group>
          </div>
          <button type="button" class="rcx-more" :class="{'rcx-more--open': showMoreFilters}" @click="showMoreFilters = !showMoreFilters">
            <i class="fas mr-25" :class="showMoreFilters ? 'fa-chevron-up' : 'fa-sliders-h'"/>
            {{ showMoreFilters ? msg('Less filters') : msg('More filters') }}
          </button>
        </div>

        <!-- advanced filters, tucked away until needed -->
        <b-collapse v-model="showMoreFilters">
          <div class="rcx-grid rcx-grid--secondary">
            <div class="rcx-field rcx-field--range">
              <label class="rcx-label"><i class="fas fa-home mr-25"/>{{ $t('Real estate') }}</label>
              <real-estate-picker small v-model="filter.eq.propertyId"/>
            </div>
            <div class="rcx-field">
              <label class="rcx-label"><i class="far fa-user mr-25"/>{{ msg('Firstname') }}</label>
              <b-input-group size="sm" class="rcx-group">
                <b-input-group-prepend is-text><i class="far fa-user"/></b-input-group-prepend>
                <b-form-input v-model="filter.eq.client.firstname"/>
              </b-input-group>
            </div>
            <div class="rcx-field">
              <label class="rcx-label"><i class="far fa-user mr-25"/>{{ msg('Lastname') }}</label>
              <b-input-group size="sm" class="rcx-group">
                <b-input-group-prepend is-text><i class="far fa-id-badge"/></b-input-group-prepend>
                <b-form-input v-model="filter.eq.client.lastname"/>
              </b-input-group>
            </div>
            <div class="rcx-field">
              <label class="rcx-label"><i class="far fa-envelope mr-25"/>{{ msg('Email') }}</label>
              <b-input-group size="sm" class="rcx-group">
                <b-input-group-prepend is-text><i class="far fa-envelope"/></b-input-group-prepend>
                <b-form-input v-model="filter.eq.client.email"/>
              </b-input-group>
            </div>
            <div class="rcx-field">
              <label class="rcx-label"><i class="fas fa-barcode mr-25"/>{{ msg('External id') }}</label>
              <b-input-group size="sm" class="rcx-group">
                <b-input-group-prepend is-text><i class="fas fa-barcode"/></b-input-group-prepend>
                <b-form-input v-model="filter.like.platformReservationId"/>
              </b-input-group>
            </div>
            <div class="rcx-field">
              <label class="rcx-label"><i class="far fa-clock mr-25"/>{{ msg('EndDate') }}</label>
              <v-select :dir="isRTL ? 'rtl' : 'ltr'" :clearable="true" v-model="temp" :options="[{value:getUnderYear,label:msg('Under 1 year')},{value:getBetween1And2,label:msg('Between 1 and 2 years')},{value:over2Years,label:msg('Over 2 years')}]" class="rcx-select w-100" label="label" :reduce="(v) => v.value" />
            </div>
          </div>
        </b-collapse>

        <!-- active filters as removable chips -->
        <div v-if="activeFilters.length" class="rcx-chips">
          <span v-for="chip in activeFilters" :key="chip.key" class="rcx-chip">
            <span class="rcx-chip__label">{{ chip.label }}</span>
            <span v-if="chip.value" class="rcx-chip__value">{{ chip.value }}</span>
            <button type="button" class="rcx-chip__x" @click="chip.clear()"><i class="fas fa-times"/></button>
          </span>
          <button type="button" class="rcx-chips__clear" @click="clearAllFilters">{{ msg('Clear all') }}</button>
        </div>
      </div>
    </div>

    <!-- Table Card -->
    <b-card no-body class="rc-table-card">
      <b-card-body>
        <!-- Table Controls -->
        <div class="d-flex align-items-center justify-content-between mb-1 flex-wrap">
          <div class="d-flex align-items-center">
            <label class="rc-label mb-0 mr-50">{{ msg('Show') }}</label>
            <v-select
                v-model="page.amount"
                :dir="isRTL ? 'rtl' : 'ltr'"
                :options="[10, 25, 50, 100]"
                :clearable="false"
                class="rc-page-select d-inline-block"
                @input="onPerPageChange"
            />
            <label class="rc-label mb-0 ml-50">{{ msg('entries') }}</label>
          </div>
          <div class="rc-total-badge">
            <i class="fas fa-list-ol mr-50"></i>
            <strong>{{ msg('Total') }}:</strong> {{ page.total }}
          </div>
        </div>

        <!-- Table -->
        <div v-handy-scroll class="table-responsive">
          <b-table
              ref="contracts-table"
              striped
              hover
              responsive
              v-bind:items="contracts"
              v-bind:fields="fields"
              :busy="loading"
              @sort-changed="onSort"
              @row-clicked="onRowClick"
              class="rc-table"
              thead-class="rc-thead"
              tbody-tr-class="rc-row"
          >
            <template #table-busy>
              <div class="text-center text-primary my-3">
                <b-spinner class="align-middle mx-1"></b-spinner>
                <strong>{{ msg('Loading') }}...</strong>
              </div>
            </template>

            <template #cell(status)="data">
              <b-badge pill :variant="getStatusVariant(data.value)" class="rc-status-badge">
                <i :class="getStatusIcon(data.value)" class="mr-25"></i>
                {{ msg(data.value) }}
              </b-badge>
            </template>

            <template #cell(id)="data">
              <b-button variant="light" size="sm" class="rc-view-btn" @click.stop="openSideBar(data.value)" v-b-tooltip.hover :title="msg('Quick view')">
                <i class="fas fa-eye"></i>
              </b-button>
            </template>

            <template #cell(creationTime)="data">
              <span class="rc-date-text">{{ $moment(data.value).format('DD/MM/YY HH:mm') }}</span>
            </template>

            <template #cell(propertyId)="data">
              <div v-if="realEstates[data.value] != null" class="d-flex align-items-center">
                <b-img
                    v-bind="{ blank: (realEstates[data.value].attachments == null || realEstates[data.value].attachments[0] == null), blankColor: '#777', width: 42, height: 42 }"
                    v-bind:src="getImageSrc(realEstates[data.value])"
                    blank-color="#475F7B"
                    rounded
                    alt="Property"
                    class="rc-property-img mr-75"
                />
                <div class="rc-property-text">
                  <div class="rc-property-addr">{{ realEstates[data.value].address }} {{ realEstates[data.value].house }}/{{ realEstates[data.value].apt }}</div>
                  <div class="rc-property-city">{{ realEstates[data.value].city }}</div>
                </div>
              </div>
            </template>

            <template #cell(bnbStyleRent)="data">
              <platform-logo v-if="data.value.platform != null" v-bind:platform="data.value.platform">
                {{ data.value.platform }}
              </platform-logo>
            </template>

            <template #cell(client)="data">
              <div class="rc-client-cell">
                <i class="fas fa-user-circle rc-client-icon mr-50"></i>
                <div>
                  <div class="rc-client-name">{{ data.value.firstname }} {{ data.value.lastname }}</div>
                  <div class="rc-client-country">{{ data.value.address.country }}</div>
                </div>
              </div>
            </template>

            <template #cell(moveDate)="data">
              <div class="d-flex align-items-center" style="gap:6px">
                <span v-if="data.item.moveDate && data.item.moveDate !== ''" class="rc-date-pill rc-date-checkin">
                  <i class="fas fa-sign-in-alt mr-25"></i>{{ $moment(data.item.moveDate).format('DD.MM.YY') }}
                </span>
                <span v-else class="text-muted" style="font-size:11px">-</span>
                <i class="fas fa-arrow-right" style="font-size:9px;opacity:.4"></i>
                <span v-if="data.item.endDate && data.item.endDate !== ''" class="rc-date-pill" :class="'rc-date-' + getEndDateColor(data.item.endDate)">
                  <i class="fas fa-sign-out-alt mr-25"></i>{{ $moment(data.item.endDate).format('DD.MM.YY') }}
                </span>
                <span v-else class="text-muted" style="font-size:11px">-</span>
              </div>
            </template>

            <template #cell(alerts)="data">
              <div v-if="data.value != null" class="rc-progress-wrap">
                <b-progress height="8px" :variant="variantColorByProgress[getProgressValue(data.item)]" :value="getProgressValue(data.item)" :max="100" class="rc-progress"></b-progress>
                <span class="rc-progress-label">{{ getProgressValue(data.item) }}%</span>
              </div>
            </template>

            <template #cell(price)="data">
              <span class="rc-price" v-if="data.item.bnbStyleRent!=null">{{ convertToEURO(data.item.bnbStyleRent.totalPayment) }}</span>
              <span class="rc-price" v-else>{{ data.value }}</span>
            </template>

            <template #cell(active)="data">
              <span v-if="data.item.allowEarlyCheckIn === true" class="rc-special-badge rc-special-yes">
                <i class="fas fa-clock mr-25"></i>Early
              </span>
              <span v-else-if="data.item.allowLateCheckOut === true" class="rc-special-badge rc-special-yes">
                <i class="fas fa-moon mr-25"></i>Late
              </span>
              <span v-else class="rc-special-badge rc-special-no">
                <i class="fas fa-minus"></i>
              </span>
            </template>

            <template #cell(depositPaid)="data">
              <i :class="data.value ? 'fas fa-check-circle rc-check-yes' : 'fas fa-times-circle rc-check-no'"></i>
            </template>

            <template #cell(depositRefunded)="data">
              <i :class="data.value ? 'fas fa-check-circle rc-check-yes' : 'fas fa-times-circle rc-check-no'"></i>
            </template>
          </b-table>
        </div>

        <!-- Pagination -->
        <div class="d-flex align-items-center justify-content-between mt-2 flex-wrap">
          <div class="rc-page-info text-muted">
            <strong>{{ msg('Total') }}:</strong> {{ page.total }}
          </div>
          <b-pagination
              v-model="page.currentPage"
              :total-rows="page.total"
              :per-page="page.amount"
              first-number
              last-number
              @input="refresh"
              class="rc-pagination mb-0"
          />
        </div>
      </b-card-body>
    </b-card>

    <!-- Contract Rent Panel Modal -->
    <b-modal no-fade v-model="dialogs.contract" size="xl" :hide-footer="true" no-close-on-backdrop centered content-class="rc-modal-content">
      <contract-rent-panel ref="realEstatePanel" @submitted="onSubmitted"/>
      <div class="text-center mt-2">
        <b-button :disabled="savingContract" variant="success" class="rc-btn mr-1" @click="submitContract">
          <i class="fas fa-save mr-50"></i> {{ savingContract ? msg('Loading') : msg('Save') }}
        </b-button>
        <b-button :disabled="!savingContract" variant="outline-danger" class="rc-btn" @click="resetButton">
          <i class="fas fa-undo mr-50"></i> {{ msg('Reset') }}
        </b-button>
      </div>
    </b-modal>

    <!-- Busy Contracts Modal -->
    <b-modal ref="contractModal" :title="msg('Contracts')" ok-only centered content-class="rc-modal-content">
      <ul v-if="busyContracts!=null && busyContracts['id']!=null && busyContracts['id'].length>0" class="rc-busy-list">
        <li v-for="contract in busyContracts.server" :key="contract.id" class="rc-busy-item">
          <b-link @click="goToContract(contract.id)" class="font-weight-bold">{{ msg('To the contract') }}</b-link>
          <div class="text-muted mt-25">{{ contract.propertyAddress }}</div>
          <div>{{ contract.client.firstname + ' ' + contract.client.lastname }}</div>
          <div class="text-muted">{{ $moment(contract.bnbStyleRent.start, 'YYYY-MM-DD').format("DD/MM/YYYY") + ' - ' + $moment(contract.bnbStyleRent.end, 'YYYY-MM-DD').format("DD/MM/YYYY") }}</div>
        </li>
      </ul>
      <p v-else class="text-muted text-center my-2">{{ msg('No active bookings for given dates') }}</p>
    </b-modal>

    <!-- Treasure Contract Panel Modal -->
    <b-modal modal-class="modal-fullscreen" no-fade v-model="dialogs.treasureContract" size="xl" :hide-footer="true" no-close-on-backdrop centered content-class="rc-modal-content">
      <treasure-contract-rent-panel ref="realEstatePanel" @submitted="onSubmitted"/>
      <div class="text-center mt-2">
        <b-button :disabled="savingContract" variant="success" class="rc-btn mr-1" @click="submitContract">
          <i class="fas fa-save mr-50"></i> {{ savingContract ? msg('Loading') : msg('Save') }}
        </b-button>
        <b-button :disabled="!savingContract" variant="outline-danger" class="rc-btn" @click="resetButton">
          <i class="fas fa-undo mr-50"></i> {{ msg('Reset') }}
        </b-button>
      </div>
    </b-modal>

    <!-- Treasure Contract Multi Modal -->
    <b-modal no-fade v-model="dialogs.treasureContractMulti" size="lg" :hide-footer="true" no-close-on-backdrop centered content-class="rc-modal-content">
      <treasure-contract-rent-panel-multi ref="realEstatePanel" @submitted="onSubmitted" @checked="contractToast"/>
    </b-modal>

    <!-- Block Calendar Modal -->
    <b-modal modal-class="modal-fullscreen" no-fade v-model="dialogs.blockCalendar" size="lg" :hide-footer="true" no-close-on-backdrop centered content-class="rc-modal-content">
      <block-dates-panel ref="blockPanel" @submitted="onSubmitted"/>
    </b-modal>

    <ContractRentSidebar ref="contractRentSidebar"/>
  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import ContractRentPanel from "@/views/realEstate/contracts/contractRentPanel";
import TreasureContractRentPanel from "@/views/realEstate/contracts/treasureContractRentPanel";
import RealEstatePicker from "@/components/components/picker/realEstatesPicker";
import CalendarPickerRange from "@/components/widgets/calendar/calendarPickerRange";
import PlatformLogo from "@/components/widgets/PlatformLogo";
import TreasureContractRentPanelMulti from "@/views/realEstate/contracts/treasureContractRentPanelMulti";
import BlockDatesPanel from "@/views/realEstate/contracts/blockDatesPanel.vue";
import mixinFilter from "@/mixins/mixinFilter";
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import ContractRentSidebar from "@/components/sidebar/ContractRent/ContractRentSidebar.vue";
import PageHero from "@/components/PageHero.vue";

const rentContractFilter = () => ({
  like: {search: '', name: "", shortDescription: ""},
  eq: {type: "", client: {}, over30Days: false, bnbStyleRent: {platform: null}},
  lte: {}, gte: {}, in: {}
});

export default {
  name: "RealEstateRentContracts",
  mixins: [mixinFilter],
  components: {
    PageHero,
    ContractRentSidebar,
    BlockDatesPanel,
    ContractRentPanel,
    TreasureContractRentPanel,
    TreasureContractRentPanelMulti,
    RealEstatePicker,
    CalendarPickerRange,
    PlatformLogo
  },
  data() {
    return {
      datesRange: null,
      loading: true,
      busyContracts: null,
      savingContract: false,
      amount: 10,
      contracts: [],
      realEstates: {},
      dialogs: {contract: false, treasureContract: false, treasureContractMulti: false, blockCalendar: false},
      temp: {},
      filter: rentContractFilter(),
      stickyFilter: false,
      showMoreFilters: false,
      sort: {by: 'creationTime', desc: true, direction: 'desc'},
      page: {amount: 10, currentPage: 1, total: 0},
      listeners: {
        refresh: () => {
        }
      }
    }
  },
  watch: {
    filter: {
      deep: true,
      handler() {
        this.refresh()
      }
    },
    datesRange: {
      handler() {
        this.refresh()
      }
    },
  },
  created() {
    let $this = this;
    this.filter = this.loadCachedFilters(rentContractFilter);
    this.refresh()
    this.$root.$on('crm::branch-updated', (this.listeners.refresh = () => {
      $this.refresh()
    }))
  },
  computed: {
    ...mapGetters('data', ['getOrganizationWebSite', 'getRealEstatesWebSite']),
    ...mapGetters('realEstateContracts', ['allStatus']),
    ...mapGetters('branches', ['allBranches']),
    isApaleoBranchOnly() {
      return this.allBranches.length > 0 && this.allBranches.every(branch => branch.name === 'APALEO');
    },
    allContractStatus() {
      return this.allStatus.map(status => ({
        value: status.value,
        text: this.msg(status.text)
      }))
    },
    getUnderYear() {
      return this.$moment().add(1, 'year').format('YYYY-MM-DD');
    },
    getBetween1And2() {
      return this.$moment().add(1, 'year').format('YYYY-MM-DD');
    },
    over2Years() {
      return this.$moment().add(1, 'year').format('YYYY-MM-DD');
    },
    variantColorByProgress() {
      return {
        100: 'success',
        75: 'warning',
        50: 'warning',
        25: 'danger',
        0: 'danger'
      };
    },
    /**
     * The applied filters as removable chips. Each chip clears exactly the
     * model the input writes — the deep filter watcher then refreshes the
     * list, so behaviour is identical to editing the field itself.
     */
    activeFilters() {
      const $this = this
      const chips = []
      const like = this.filter.like || {}
      const eq = this.filter.eq || {}
      const client = eq.client || {}
      const statuses = Array.isArray(this.filter.in && this.filter.in.status) ? this.filter.in.status : []

      if (like.id) chips.push({key: 'id', label: this.msg('Id'), value: like.id, clear: () => $this.$set($this.filter.like, 'id', '')})
      if (statuses.length) {
        const names = statuses.map(value => (this.allContractStatus.find(s => s.value === value) || {}).text || value)
        chips.push({
          key: 'status', label: this.msg('Status'),
          value: names.slice(0, 3).join(', ') + (names.length > 3 ? ` +${names.length - 3}` : ''),
          clear: () => $this.$set($this.filter.in, 'status', []),
        })
      }
      if (eq.bnbStyleRent && eq.bnbStyleRent.platform) {
        chips.push({key: 'platform', label: this.msg('Platform'), value: eq.bnbStyleRent.platform, clear: () => $this.$set($this.filter.eq.bnbStyleRent, 'platform', null)})
      }
      if (eq.propertyId) chips.push({key: 'property', label: this.$t('Real estate'), value: null, clear: () => $this.$set($this.filter.eq, 'propertyId', null)})
      if (client.firstname) chips.push({key: 'firstname', label: this.msg('Firstname'), value: client.firstname, clear: () => $this.$set($this.filter.eq.client, 'firstname', '')})
      if (client.lastname) chips.push({key: 'lastname', label: this.msg('Lastname'), value: client.lastname, clear: () => $this.$set($this.filter.eq.client, 'lastname', '')})
      if (client.email) chips.push({key: 'email', label: this.msg('Email'), value: client.email, clear: () => $this.$set($this.filter.eq.client, 'email', '')})
      if (like.platformReservationId) chips.push({key: 'external', label: this.msg('External id'), value: like.platformReservationId, clear: () => $this.$set($this.filter.like, 'platformReservationId', '')})
      if (this.datesRange && (this.datesRange.start || this.datesRange.end)) {
        chips.push({key: 'range', label: this.msg('checkinrange'), value: `${this.datesRange.start || '…'} → ${this.datesRange.end || '…'}`, clear: () => { $this.datesRange = null }})
      }
      if (typeof this.temp === 'string' && this.temp) {
        chips.push({key: 'endDate', label: this.msg('EndDate'), value: null, clear: () => { $this.temp = null }})
      }
      return chips
    },
    fields() {
      let $this = this
      return [
        {key: 'status', label: 'Status', sortable: true, thClass: 'rc-th', tdClass: 'rc-td'},
        {key: 'id', label: 'View', sortable: true, thClass: 'rc-th rc-th-center', tdClass: 'rc-td text-center'},
        {key: 'creationTime', label: $this.msg('Creation Time'), sortable: true, thClass: 'rc-th', tdClass: 'rc-td'},
        {key: 'propertyId', label: $this.msg('Address'), sortable: true, thClass: 'rc-th', tdClass: 'rc-td'},
        {key: 'bnbStyleRent', label: $this.msg('Platform'), sortable: true, thClass: 'rc-th', tdClass: 'rc-td'},
        {key: 'platformReservationId', label: $this.msg('Platform Id'), sortable: true, thClass: 'rc-th', tdClass: 'rc-td'},
        {key: 'client', label: $this.msg('Client'), sortable: false, thClass: 'rc-th', tdClass: 'rc-td'},
        {key: 'alerts', label: $this.msg('Progress'), sortable: false, thClass: 'rc-th', tdClass: 'rc-td'},
        {key: 'price', label: $this.msg('Rental price'), sortable: false, thClass: 'rc-th', tdClass: 'rc-td'},
        {key: 'moveDate', label: $this.msg('CheckIn - CheckOut'), sortable: false, thClass: 'rc-th', tdClass: 'rc-td'},
        {key: 'active', label: $this.msg('Special requests'), sortable: false, thClass: 'rc-th', tdClass: 'rc-td'},
        {key: 'numberOfGuests', label: $this.msg('numberOfGuests'), sortable: false, thClass: 'rc-th rc-th-center', tdClass: 'rc-td text-center'},
        {key: 'depositPaid', label: $this.msg('Deposit paid'), sortable: true, thClass: 'rc-th rc-th-center', tdClass: 'rc-td text-center'},
        {key: 'depositRefunded', label: $this.msg('Deposit refunded'), sortable: true, thClass: 'rc-th rc-th-center', tdClass: 'rc-td text-center'},
      ]
    },
  },
  methods: {
    ...mapActions('realEstateContracts', ['getAllContracts', 'sendSMSCheckIn']),
    /** Everything back to default — same reset the old ✕ did, plus dates and the end-date bucket. */
    clearAllFilters() {
      this.resetFilters()
      this.datesRange = null
      this.temp = null
    },
    getStatusVariant(status) {
      const map = { ACTIVE: 'success', CANCELED: 'danger', REVIEWING: 'secondary', NEW: 'warning', INQUIRY: 'warning' }
      return map[status] || 'info'
    },
    getStatusIcon(status) {
      const map = { ACTIVE: 'fas fa-check', CANCELED: 'fas fa-ban', REVIEWING: 'fas fa-search', NEW: 'fas fa-star', INQUIRY: 'fas fa-question' }
      return map[status] || 'fas fa-info-circle'
    },
    goToContract(id) {
      window.open(`/real_estate/contracts/rent/${id}`, '_blank');
    },
    openSideBar(id) {
      this.$refs.contractRentSidebar.showContract(id)
    },
    convertToEURO(someNumber) {
      return new Intl.NumberFormat("de-AT", {
        style: "currency",
        currency: "EUR",
      }).format(someNumber);
    },
    gradientColor(progress) {
      const hue = ((1 - progress / 100) * 120).toString(10);
      return `hsl(${hue}, 100%, 50%)`;
    },
    getEndDateColor(endDate) {
      let today = this.$moment();
      let diffDays = this.$moment(endDate).diff(today, 'days');
      if (diffDays < 365) return "danger"
      else if (diffDays < 730) return "warning"
      return "success"
    },
    onContractGenerate() {
      this.dialogs.contract = true
    },
    onTreasureContractGenerateMulti() {
      this.dialogs.treasureContractMulti = true
    },
    onBlockCalendar() {
      this.dialogs.blockCalendar = true
    },
    onTreasureContractGenerate() {
      this.dialogs.treasureContract = true
    },
    /** v-select does not trigger b-pagination @input — refetch when page size changes */
    onPerPageChange(value) {
      const n = typeof value === 'number' ? value : parseInt(String(value), 10)
      if (!Number.isNaN(n)) {
        this.page.amount = n
      }
      this.page.currentPage = 1
      this.refresh()
    },
    refresh() {
      let $this = this
      this.loading = true;
      if (this.datesRange != null) {
        this.filter.gte.moveDate = this.datesRange.start
        this.filter.lte.moveDate = this.datesRange.end
      } else {
        this.filter.gte.moveDate = null
        this.filter.lte.moveDate = null
      }
      this.filter.eq.over30Days = false;
      this.getAllContracts({
        page: $this.page.currentPage,
        amount: $this.page.amount,
        filter: $this.filter,
        sortcolumn: 'creationTime',
        sortby: 'desc',
      }).then(this.onContractsRetrieve, (error) => $this.error = error)
    },
    onContractsRetrieve(data) {
      this.page = {
        amount: data['Amount'],
        total: data['Total'],
        currentPage: data['Page']
      };
      this.sort = {
        by: data["sortColumn"],
        direction: data["sortOrder"]
      }
      this.contracts = data.contracts.filter(contract => contract.status !== "INQUIRY");
      this.realEstates = (data.realEstates || []).toObject("id");
      this.loading = false;
    },
    getImageSrc(property) {
      if (property == null || property.attachments == null || property.attachments[0] == null)
        return null
      return (property.attachments[0].attachmentUrl.startsWith('http') ? '' : this.getRealEstatesWebSite) + property.attachments[0].attachmentUrl;
    },
    onSort(data) {
      this.sort.by = data.sortBy;
      this.sort.direction = data.sortDesc ? "desc" : "asc";
      this.refresh();
    },
    onRowClick(contract) {
      window.open(`/real_estate/contracts/rent/${contract.id}`, '_blank');
    },
    onClientSelect(clientId) {
      this.$root.$emit('bv::hide::popover')
      this.$root.$emit('crm::client::select', clientId)
    },
    submitContract() {
      this.savingContract = true;
      this.$root.$emit('crm::submitContract')
    },
    checkAvailability() {
      this.$root.$emit('crm::checkContract')
    },
    resetButton() {
      this.savingContract = false;
    },
    onSubmitted(ans) {
      if (ans.status) {
        this.refresh()
        this.dialogs.contract = false;
        this.dialogs.treasureContract = false;
        this.dialogs.treasureContractMulti = false;
        this.dialogs.blockCalendar = false;
        this.savingContract = false;
      }
    },
    contractToast(ans) {
      console.log("contractToast : ", ans);
      if (ans == null) {
        this.$bvModal.msgBoxOk('No contracts on these dates', {
          title: 'No Contracts',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'success'
        });
      } else {
        this.busyContracts = ans;
        console.log("busyContracts length : ", this.busyContracts.length);
        this.$refs.contractModal.show();
      }
    },
    sendCheckInDetailsButtonSMS(id) {
      this.sendSMSCheckIn(id);
    },
    getProgressValue(contract) {
      var sum = 100;
      let isSignedValue = contract.signedOffer ? 0 : 25;
      if (contract.alerts == null || contract.alerts.length === 0)
        return 100 - isSignedValue;
      for (let i = 0; i < contract.alerts.length; i++) {
        if (contract.alerts[i].actionRequired === "RENT_PAYMENT") {
          sum -= 25;
        } else if (contract.alerts[i].actionRequired === "AUTHORIZATION_FEE_PAYMENT") {
          sum -= 25;
        } else if (contract.alerts[i].actionRequired === "UPLOAD_REQUIRED_FILES") {
          sum -= 25;
        }
      }
      return sum - isSignedValue;
    },
  }
}
</script>

<style scoped>
.rc-page {
  padding-bottom: 20px;
  background: radial-gradient(1200px 400px at 20% 0%, rgba(115, 103, 240, .05), transparent 60%),
  radial-gradient(1000px 380px at 90% 10%, rgba(0, 184, 148, .05), transparent 55%);
  border-radius: 18px;
}

/* Action buttons */
.rc-btn { border-radius: 12px; font-weight: 700; font-size: 12px; }

/* Filters */
.rc-filter-wrap.sticky { position: sticky; z-index: 999; top: 80px; }
/* overflow:visible so v-calendar range popover is not clipped (popover uses fixed strategy + z-index in calendarPickerRange) */
.rc-filter-card { border-radius: 18px; overflow: visible; border: 1px solid rgba(0,0,0,.06); box-shadow: 0 8px 20px rgba(0,0,0,.05); }
.rc-filter-icon { color: #7367f0; font-size: 13px; }
.rc-filter-title { font-weight: 900; font-size: 13px; }
.rc-filter-action { color: inherit; opacity: .5; transition: opacity .12s; font-size: 13px; }
.rc-filter-action:hover { opacity: 1; }
.rc-pin { color: inherit; opacity: .35; transition: all .12s; }
.rc-pin-active { color: #7367f0; opacity: 1; }
.rc-filter-grid { row-gap: 6px; }
.rc-filter-col { margin-bottom: 4px; }
.rc-label { font-size: 11px; font-weight: 700; opacity: .7; margin-bottom: 2px; }
.rc-input { border-radius: 10px; }
.rc-select :deep(.vs__dropdown-toggle) { border-radius: 10px; }

/* Table Card */
.rc-table-card { border-radius: 18px; overflow: hidden; border: 1px solid rgba(0,0,0,.06); box-shadow: 0 10px 26px rgba(0,0,0,.06); }
.rc-page-select { width: 75px; min-width: 70px; }
.rc-page-select :deep(.vs__dropdown-toggle) {
  border-radius: 10px;
  min-height: 32px !important;
  max-height: 32px !important;
  padding: 0 6px !important;
}
.rc-page-select :deep(.vs__selected),
.rc-page-select :deep(.vs__search) {
  margin: 0 !important;
  padding: 0 !important;
  line-height: 1.35 !important;
}
.rc-page-select :deep(.vs__actions) { padding: 0 4px 0 0 !important; }
.rc-total-badge { font-size: 12px; padding: 6px 14px; border-radius: 999px; background: rgba(115, 103, 240, .08); border: 1px solid rgba(115, 103, 240, .15); color: #7367f0; font-weight: 700; }

/* Table */
.rc-table :deep(thead) { background: rgba(0,0,0,.02); }
.rc-th { font-size: 11px !important; font-weight: 800 !important; text-transform: uppercase; letter-spacing: .5px; white-space: nowrap; padding: 10px 12px !important; }
.rc-th-center { text-align: center !important; }
.rc-td { padding: 10px 12px !important; vertical-align: middle !important; }
.rc-table :deep(tbody tr) { cursor: pointer; transition: background .12s; }
.rc-table :deep(tbody tr:hover) { background: rgba(115, 103, 240, .04) !important; }

/* Status badge */
.rc-status-badge { font-weight: 700; font-size: 11px; padding: 5px 12px; border-radius: 999px; }

/* View button */
.rc-view-btn { width: 34px; height: 34px; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center; background: rgba(115, 103, 240, .08); border: 1px solid rgba(115, 103, 240, .15); color: #7367f0; transition: all .12s; }
.rc-view-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 16px rgba(115, 103, 240, .2); background: rgba(115, 103, 240, .14); color: #7367f0; }

/* Date */
.rc-date-text { font-size: 12px; font-weight: 600; white-space: nowrap; }

/* Property */
.rc-property-img { width: 42px; height: 42px; object-fit: cover; border-radius: 10px; flex-shrink: 0; }
.rc-property-text { min-width: 0; }
.rc-property-addr { font-weight: 800; font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 180px; }
.rc-property-city { font-size: 11px; opacity: .6; }

/* Client */
.rc-client-cell { display: flex; align-items: center; }
.rc-client-icon { font-size: 18px; opacity: .4; }
.rc-client-name { font-weight: 800; font-size: 12px; white-space: nowrap; }
.rc-client-country { font-size: 11px; opacity: .6; }

/* Date pills */
.rc-date-pill { display: inline-flex; align-items: center; font-size: 11px; font-weight: 700; padding: 4px 10px; border-radius: 999px; white-space: nowrap; }
.rc-date-checkin { background: rgba(40, 199, 111, .12); border: 1px solid rgba(40, 199, 111, .22); color: #28c76f; }
.rc-date-danger { background: rgba(234, 84, 85, .12); border: 1px solid rgba(234, 84, 85, .22); color: #ea5455; }
.rc-date-warning { background: rgba(255, 159, 67, .12); border: 1px solid rgba(255, 159, 67, .22); color: #ff9f43; }
.rc-date-success { background: rgba(40, 199, 111, .12); border: 1px solid rgba(40, 199, 111, .22); color: #28c76f; }

/* Progress */
.rc-progress-wrap { display: flex; align-items: center; gap: 8px; min-width: 100px; }
.rc-progress { border-radius: 999px; flex: 1; }
.rc-progress :deep(.progress-bar) { border-radius: 999px; }
.rc-progress-label { font-size: 11px; font-weight: 800; white-space: nowrap; min-width: 30px; }

/* Price */
.rc-price { font-weight: 800; font-size: 13px; white-space: nowrap; }

/* Special requests */
.rc-special-badge { display: inline-flex; align-items: center; font-size: 11px; font-weight: 700; padding: 4px 10px; border-radius: 999px; }
.rc-special-yes { background: rgba(40, 199, 111, .12); border: 1px solid rgba(40, 199, 111, .22); color: #28c76f; }
.rc-special-no { background: rgba(0,0,0,.04); border: 1px solid rgba(0,0,0,.08); color: rgba(0,0,0,.3); }

/* Check icons */
.rc-check-yes { font-size: 1.3rem; color: #28c76f; }
.rc-check-no { font-size: 1.3rem; color: #ea5455; opacity: .6; }

/* Pagination */
.rc-pagination :deep(.page-link) { border-radius: 10px !important; }
.rc-page-info { font-size: 12px; }

/* Modal */
.rc-modal-content { border-radius: 18px; overflow: hidden; }

/* Busy contracts list */
.rc-busy-list { list-style: none; padding: 0; margin: 0; }
.rc-busy-item { padding: 12px; border-radius: 14px; border: 1px solid rgba(0,0,0,.08); background: rgba(0,0,0,.01); margin-bottom: 10px; }
</style>

<style lang="scss" scoped>
/* ═══ Filter workspace v2 ═══ */
.rcx-filter {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, .06);
  border-radius: 16px;
  padding: 14px 18px 14px;
  margin-bottom: 14px;
  box-shadow: 0 2px 10px rgba(15, 23, 42, .04);
}

/* header */
.rcx-filter__head { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; }
.rcx-filter__badge {
  width: 30px; height: 30px; border-radius: 9px; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(115, 103, 240, .12); color: #7367f0; font-size: .8rem;
}
.rcx-filter__title { font-size: .82rem; font-weight: 800; text-transform: uppercase; letter-spacing: .06em; color: #475569; }
.rcx-filter__line { flex: 1; height: 1px; background: linear-gradient(90deg, rgba(100, 116, 139, .18), transparent); }
.rcx-icon-btn {
  width: 30px; height: 30px; border: none; border-radius: 9px; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(15, 23, 42, .05); color: #64748b; font-size: .74rem;
  transition: background .15s, color .15s, transform .15s;
  &:hover { background: rgba(115, 103, 240, .14); color: #7367f0; transform: translateY(-1px); }
  &:focus { outline: none; }
  &--on { background: rgba(115, 103, 240, .16); color: #7367f0; }
}

/* grids */
.rcx-grid { display: grid; gap: 12px 14px; align-items: end; }
.rcx-grid--primary { grid-template-columns: minmax(240px, 1.4fr) 1fr 1fr 1fr auto; }
.rcx-grid--secondary {
  grid-template-columns: minmax(220px, 1.3fr) 1fr 1fr 1fr 1fr 1fr;
  margin-top: 12px; padding-top: 12px;
  border-top: 1px dashed rgba(15, 23, 42, .08);
}
@media (max-width: 1350px) {
  .rcx-grid--primary { grid-template-columns: 1fr 1fr; }
  .rcx-grid--secondary { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 640px) {
  .rcx-grid--primary, .rcx-grid--secondary { grid-template-columns: 1fr; }
}

/* fields */
.rcx-field { min-width: 0; }
.rcx-label {
  display: flex; align-items: center;
  font-size: .66rem; font-weight: 800; text-transform: uppercase; letter-spacing: .06em;
  color: #94a3b8; margin-bottom: 5px;
  i { opacity: .7; }
}
/* one height for every control in the workspace — inputs, groups, selects */
.rcx-field :deep(.form-control) { height: 34px; }
/* the date-range picker builds its own trigger pills — same 34px baseline */
.rcx-field :deep(.cal-range-picker__row) { margin-bottom: 0; }
.rcx-field :deep(.cal-range-picker__field) { height: 34px; padding: 0 12px; }
.rcx-group :deep(.input-group-text) {
  height: 34px;
  background: rgba(15, 23, 42, .03); border-color: rgba(15, 23, 42, .1);
  color: #94a3b8; font-size: .7rem;
}
.rcx-group :deep(.form-control) { border-color: rgba(15, 23, 42, .1); }
.rcx-select :deep(.vs__dropdown-toggle) { border-color: rgba(15, 23, 42, .1); min-height: 34px; }
.rcx-range { position: relative; display: flex; align-items: center; }
.rcx-range__clear {
  position: absolute; right: 6px; top: 50%; transform: translateY(-50%);
  width: 20px; height: 20px; border: none; border-radius: 50%; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(234, 84, 85, .12); color: #ea5455; font-size: .6rem;
  &:hover { background: rgba(234, 84, 85, .22); }
  &:focus { outline: none; }
}

/* the More-filters toggle sits at the end of the primary row */
.rcx-more {
  justify-self: end; align-self: end;
  border: 1px dashed rgba(115, 103, 240, .45); border-radius: 999px;
  background: transparent; color: #7367f0; cursor: pointer;
  font-size: .74rem; font-weight: 700; white-space: nowrap;
  padding: 7px 14px; transition: background .15s, border-color .15s;
  &:hover { background: rgba(115, 103, 240, .08); border-color: #7367f0; }
  &:focus { outline: none; }
  &--open { background: rgba(115, 103, 240, .1); border-style: solid; }
}

/* active chips */
.rcx-chips {
  display: flex; align-items: center; flex-wrap: wrap; gap: 8px;
  margin-top: 12px; padding-top: 12px;
  border-top: 1px dashed rgba(15, 23, 42, .08);
}
.rcx-chip {
  display: inline-flex; align-items: center; gap: 6px;
  background: rgba(115, 103, 240, .08);
  border: 1px solid rgba(115, 103, 240, .22);
  border-radius: 999px; padding: 4px 6px 4px 11px;
  font-size: .72rem; line-height: 1;
}
.rcx-chip__label { font-weight: 800; color: #7367f0; text-transform: uppercase; letter-spacing: .03em; font-size: .62rem; }
.rcx-chip__value { font-weight: 600; color: #475569; max-width: 220px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.rcx-chip__x {
  width: 18px; height: 18px; border: none; border-radius: 50%; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(115, 103, 240, .16); color: #7367f0; font-size: .56rem;
  transition: background .15s, color .15s;
  &:hover { background: #ea5455; color: #fff; }
  &:focus { outline: none; }
}
.rcx-chips__clear {
  border: none; background: transparent; cursor: pointer;
  font-size: .72rem; font-weight: 700; color: #ea5455;
  padding: 4px 8px; border-radius: 8px;
  &:hover { background: rgba(234, 84, 85, .1); }
  &:focus { outline: none; }
}
</style>

<style lang="scss">
/* filter workspace v2 — dark mode */
.dark-layout {
  .rcx-filter { background: #283046; border-color: rgba(255, 255, 255, .07); box-shadow: 0 2px 10px rgba(0, 0, 0, .2); }
  .rcx-filter__title { color: #b4b7bd; }
  .rcx-filter__line { background: linear-gradient(90deg, rgba(255, 255, 255, .12), transparent); }
  .rcx-icon-btn { background: rgba(255, 255, 255, .06); color: #94a3b8; }
  .rcx-label { color: #8a92a6; }
  .rcx-group .input-group-text { background: rgba(255, 255, 255, .04); border-color: rgba(255, 255, 255, .12); color: #8a92a6; }
  .rcx-grid--secondary, .rcx-chips { border-color: rgba(255, 255, 255, .1); }
  .rcx-chip { background: rgba(115, 103, 240, .14); border-color: rgba(115, 103, 240, .35); }
  .rcx-chip__value { color: #d0d2d6; }
}
</style>
